/**
 * The contact fields of the ONE details form (docs-company ADR-110
 * Amendment 6, Amendment 8): first name, last name, email and phone,
 * labelled, limited and checked the same way whether the visitor is leaving
 * their details (lead.ts, POST /chat/lead) or giving them for a booking
 * (actions.ts, `collect_details`). DetailsForm.tsx draws both from here.
 *
 * Until Amendment 6 each form kept its own copy, and they drifted: the lead's
 * phone read "Phone (optional)" beside the booking's "Phone *", the lead took
 * an email of 200 characters and the booking one of 254, and a visitor who
 * had just filled in one was handed the other. Pure, so
 * scripts/test-config.mjs pins the rules against the built artefact.
 *
 * 🚨 THE NAME IS TWO BOXES AND ONE VALUE (Amendment 8).
 *
 * The form asks for a first name and a last name; the wire carries the one
 * `name` both backends have always stored — "Ada Lovelace" — joined here by
 * `joinName`. Nothing downstream changes: chat_lead, the notification e-mail,
 * Ema Contacts and the booking core all keep the field they know.
 *
 * What the split buys is the FIRST NAME, exactly. A single box was answered
 * with "Ada", "Ada Lovelace", "Mrs Lovelace" and "lovelace ada", and the
 * assistant's own rule for addressing a visitor (chat_message's
 * `_lead_first_name`) takes the first whitespace token of whatever arrived.
 * With two boxes that token is the first name the visitor typed, so being
 * greeted by name stops being a guess.
 */

import {
  EMAIL_MAX,
  PERSON_NAME_MAX,
  checkEmail,
  checkPersonName,
  collapseSpaces,
  readPhone,
  type FieldProblem,
} from './fields';

export const CONTACT_FIELDS = ['firstName', 'lastName', 'email', 'phone'] as const;

export type ContactField = (typeof CONTACT_FIELDS)[number];

/**
 * The shape the find-booking form still reads a typed contact with
 * (actions.ts `validateLookup`): it must match what the booking core stored,
 * not what the details form now asks. The details form itself holds an email
 * to the shared rule (fields.ts `checkEmail`, the server's).
 */
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * What a phone number is WRITTEN with — the find-booking form's reading of a
 * number typed at booking time (actions.ts). The details form holds a phone
 * to its COUNTRY's rule instead (fields.ts `readPhone`, the shared library's).
 */
export const PHONE_RE = /^\+?[0-9][0-9 ().\/-]{3,}(?:\s*(?:x|ext\.?|#)\s*[0-9]{1,6})?$/i;

export function isPhone(value: string): boolean {
  return PHONE_RE.test(value) && (value.match(/[0-9]/g) ?? []).length >= 6;
}

/**
 * The server's limits (chat_lead since 1.31.0, shared-chatbot#577): a first
 * and a last name of 50 each, an email of 254. A phone's box is as long as
 * its country's longest number (fields.ts `phoneInputMax`); 20 here is the
 * box before a country is known, and a paste past any of them is taken whole
 * and refused in words rather than cut by the browser.
 */
export const CONTACT_LIMITS: Readonly<Record<ContactField, number>> = {
  firstName: PERSON_NAME_MAX,
  lastName: PERSON_NAME_MAX,
  email: EMAIL_MAX,
  phone: 20,
};

/** How long the joined name can be: two boxes of 50 and the space between. */
export const NAME_MAX = 2 * PERSON_NAME_MAX + 1;

/** The one `name` the wire carries, from the two boxes the form draws. */
export function joinName(firstName = '', lastName = ''): string {
  return [firstName, lastName].map((part) => collapseSpaces(part)).filter(Boolean).join(' ');
}

export type ContactValues = Partial<Record<ContactField, string>>;

/**
 * Why a box was refused: the broad reason, and the rule it broke — the shared
 * library's problem `{code, params}` (shared-audit#17), which
 * fieldMessages.ts turns into the widget's sentence in the visitor's language.
 */
export type ContactReason = 'required' | 'invalid' | 'tooLong';
export type ContactDetail = FieldProblem;

export type ContactValidation =
  | {
      ok: true;
      /**
       * Each field the form showed, as it is SENT: names with their spaces
       * collapsed, the email lower-cased, the phone in E.164. An empty
       * optional one is left out.
       */
      value: ContactValues;
      /** The two name boxes as one value, '' when the form showed neither. */
      name: string;
      /** The first name alone, for the words the visitor reads back. */
      firstName: string;
    }
  | { ok: false; field: ContactField; reason: ContactReason; detail?: ContactDetail };

const reasonOf = (problem: FieldProblem): ContactReason =>
  problem.code.endsWith('.required') ? 'required' : problem.code.endsWith('.too_long') ? 'tooLong' : 'invalid';

/**
 * Checked in the order the form shows the fields; the first problem wins.
 * The rules are the shared library's, as the server holds them (fields.ts):
 * a person's name, the email rule, a phone read in `phoneCountry` — which is
 * the country chosen beside the box. A field the form did not show is never
 * read.
 */
export function validateContact(
  raw: ContactValues,
  fields: readonly ContactField[],
  required: readonly ContactField[],
  phoneCountry = 'GB',
): ContactValidation {
  const value: ContactValues = {};
  for (const field of fields) {
    const typed = raw[field] ?? '';
    const empty = !collapseSpaces(typed);
    if (empty) {
      if (required.includes(field)) return { ok: false, field, reason: 'required' };
      continue;
    }
    const checked =
      field === 'firstName' || field === 'lastName'
        ? checkPersonName(typed)
        : field === 'email'
          ? checkEmail(typed)
          : readPhone(typed, phoneCountry);
    if (!checked.ok) return { ok: false, field, reason: reasonOf(checked.problem), detail: checked.problem };
    // As it is sent: a name trimmed with its spaces collapsed, the address
    // lower-cased, the phone in E.164.
    value[field] = checked.value;
  }
  return { ok: true, value, name: joinName(value.firstName, value.lastName), firstName: value.firstName ?? '' };
}
